import i18n from './i18n';

export const CHAT_LOTTIE_WINK_PREFIX = ':cw-';

export type LottieChatWink = {
    trigger: string;
    label: string;
    fileName: string;
};

const lottieChatWinkUrls = import.meta.glob<string>(
    '../assets/lottie/chat-winks/*.json',
    {
        eager: true,
        query: '?url',
        import: 'default',
    }
);

const lottieChatWinkThumbnailUrls = import.meta.glob<string>(
    '../assets/images/winks/chat/*.png',
    {
        eager: true,
        query: '?url',
        import: 'default',
    }
);

const animationDataCache = new Map<string, Promise<unknown>>();

const loadLottieJson = (url: string) => {
    const cachedAnimationData = animationDataCache.get(url);

    if (cachedAnimationData) {
        return cachedAnimationData;
    }

    const animationData = fetch(url).then((response) => {
        if (!response.ok) {
            throw new Error(
                `Failed to load Lottie chat wink JSON: ${response.status} ${response.statusText}`
            );
        }

        return response.json();
    });

    animationDataCache.set(url, animationData);

    return animationData;
};

export const CHAT_LOTTIE_WINKS: Record<string, LottieChatWink> = {
    'rainbow-pot-gold': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}rainbow-pot-gold`,
        label: 'Rainbow Pot Gold',
        fileName: 'rainbow-pot-gold',
    },
    'trivia-time': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}trivia-time`,
        label: 'Trivia Time',
        fileName: 'trivia-time',
    },
    'paper-bag-cat-tail': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}paper-bag-cat-tail`,
        label: 'Paper Bag Cat Tail',
        fileName: 'paper-bag-cat-tail',
    },
    'jack-o-lantern': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}jack-o-lantern`,
        label: 'Jack O Lantern',
        fileName: 'jack-o-lantern',
    },
    'countdown-3-2-1': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}countdown-3-2-1`,
        label: 'Countdown 3 2 1',
        fileName: 'countdown-3-2-1',
    },
    'vroom-checkered-flag': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}vroom-checkered-flag`,
        label: 'Vroom Checkered Flag',
        fileName: 'vroom-checkered-flag',
    },
    'coffee-bingo-ball-latte': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}coffee-bingo-ball-latte`,
        label: 'Coffee Bingo Ball Latte',
        fileName: 'coffee-bingo-ball-latte',
    },
    'bingo-dauber': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}bingo-dauber`,
        label: 'Bingo Dauber',
        fileName: 'bingo-dauber',
    },
    'winter-bingo-ball': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}winter-bingo-ball`,
        label: 'Winter Bingo Ball',
        fileName: 'winter-bingo-ball',
    },
    'summer-sunflower': {
        trigger: `${CHAT_LOTTIE_WINK_PREFIX}summer-sunflower`,
        label: 'Summer Sunflower',
        fileName: 'summer-sunflower',
    },
};

const getLottieChatWinkKey = (message?: string) => {
    if (!message?.startsWith(CHAT_LOTTIE_WINK_PREFIX)) {
        return undefined;
    }

    return message.slice(CHAT_LOTTIE_WINK_PREFIX.length);
};

export const getLottieChatWink = ({
    ch,
    message,
}: {
    ch?: boolean;
    message?: string;
}) => {
    if (!ch) {
        return undefined;
    }

    const key = getLottieChatWinkKey(message);

    return key ? CHAT_LOTTIE_WINKS[key] : undefined;
};

export const getLottieChatWinkUrl = (wink: LottieChatWink) => {
    const language = (i18n.language || i18n.resolvedLanguage || 'en')
        .toLowerCase()
        .replace('_', '-');
    const baseLanguage = language.split('-')[0];
    const getUrl = (locale?: string) =>
        lottieChatWinkUrls[
            `../assets/lottie/chat-winks/${wink.fileName}${
                locale ? `_${locale}` : ''
            }.json`
        ];

    return getUrl(language) || getUrl(baseLanguage) || getUrl() || undefined;
};

export const getLottieChatWinkThumbnailUrl = (wink: LottieChatWink) =>
    lottieChatWinkThumbnailUrls[
        `../assets/images/winks/chat/${wink.fileName}.png`
    ];

export const loadLottieChatWink = (wink: LottieChatWink) => {
    const url = getLottieChatWinkUrl(wink);

    return url ? loadLottieJson(url) : Promise.resolve(undefined);
};
